import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Settings, Zap, TrendingUp, BarChart3, Timer, DollarSign, Calendar, RefreshCw } from "lucide-react";

const TRADING_STRATEGIES = [
  {
    id: "intraday_etf_options",
    label: "Intraday ETF Options",
    description: "QQQ, SPY - Quick signals, tight timeframes, options-focused",
    icon: Zap,
    color: "text-yellow-600",
    bgColor: "bg-yellow-50",
    borderColor: "border-yellow-200"
  },
  {
    id: "swing_mega_caps",
    label: "Swing Trading Mega Caps",
    description: "2-5 day holds, larger moves, stock-focused",
    icon: TrendingUp,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200"
  },
  {
    id: "regime_timing",
    label: "Regime Timing",
    description: "Market condition detection, sector rotation",
    icon: BarChart3,
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200"
  },
  {
    id: "scalping",
    label: "Scalping",
    description: "Very short-term, high-frequency signals",
    icon: Timer,
    color: "text-red-600",
    bgColor: "bg-red-50",
    borderColor: "border-red-200"
  }
];

const ACCOUNT_SIZES = [
  { value: "5000", label: "$5,000" },
  { value: "10000", label: "$10,000" },
  { value: "25000", label: "$25,000" },
  { value: "50000", label: "$50,000" },
  { value: "100000", label: "$100,000" },
  { value: "250000", label: "$250,000+" }
];

const DTE_PREFERENCES = [
  { value: "0dte", label: "0DTE (Same Day)" },
  { value: "1-3dte", label: "1-3 DTE" },
  { value: "weekly", label: "Weekly (4-7 DTE)" },
  { value: "monthly", label: "Monthly (20-45 DTE)" }
];

const RISK_PER_TRADE = [
  { value: "0.5", label: "0.5% (Conservative)" },
  { value: "1", label: "1% (Moderate)" },
  { value: "2", label: "2% (Aggressive)" },
  { value: "3", label: "3% (High Risk)" }
];

const TradingProfileSettings = ({ onSettingsChange, onRecalculate, initialSettings }) => {
  const [settings, setSettings] = useState({
    strategy: initialSettings?.strategy || "intraday_etf_options",
    accountSize: initialSettings?.accountSize || "25000",
    dtePreference: initialSettings?.dtePreference || "1-3dte",
    riskPerTrade: initialSettings?.riskPerTrade || "1"
  });
  const [isRecalculating, setIsRecalculating] = useState(false);

  // Notify parent of settings changes
  useEffect(() => {
    if (onSettingsChange) {
      // Convert single strategy to array for backward compatibility
      onSettingsChange({
        ...settings,
        strategies: [settings.strategy]
      });
    }
  }, [settings, onSettingsChange]);

  const handleRecalculate = async () => {
    setIsRecalculating(true);
    if (onRecalculate) {
      await onRecalculate();
    }
    // Simulate minimum loading time for UX
    setTimeout(() => setIsRecalculating(false), 1000);
  };

  const getSelectedStrategy = () => {
    return TRADING_STRATEGIES.find(s => s.id === settings.strategy) || TRADING_STRATEGIES[0];
  };

  const selectedStrategy = getSelectedStrategy();
  const SelectedIcon = selectedStrategy.icon;

  return (
    <Card className="border-slate-200 bg-white/90 backdrop-blur-sm shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Settings className="w-4 h-4 text-slate-600" />
            Trading Profile
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge className={`${selectedStrategy.bgColor} ${selectedStrategy.color} border ${selectedStrategy.borderColor} text-xs`}>
              <SelectedIcon className="w-3 h-3 mr-1" />
              {selectedStrategy.label}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecalculate}
              disabled={isRecalculating}
              className="h-7 px-2 text-xs"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${isRecalculating ? 'animate-spin' : ''}`} />
              {isRecalculating ? 'Recalculating...' : 'Recalculate'}
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500">Select your trading style to align signals and recommendations</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* All Settings in One Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Trading Strategy Dropdown */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
              <Zap className="w-3 h-3" />
              Trading Strategy
            </Label>
            <Select 
              value={settings.strategy} 
              onValueChange={(value) => setSettings(prev => ({ ...prev, strategy: value }))}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select strategy" />
              </SelectTrigger>
              <SelectContent>
                {TRADING_STRATEGIES.map((strategy) => {
                  const Icon = strategy.icon;
                  return (
                    <SelectItem key={strategy.id} value={strategy.id}>
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${strategy.color}`} />
                        <span>{strategy.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-400">{selectedStrategy.description}</p>
          </div>

          {/* Account Size */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              Account Size
            </Label>
            <Select 
              value={settings.accountSize} 
              onValueChange={(value) => setSettings(prev => ({ ...prev, accountSize: value }))}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select account size" />
              </SelectTrigger>
              <SelectContent>
                {ACCOUNT_SIZES.map((size) => (
                  <SelectItem key={size.value} value={size.value}>
                    {size.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* DTE Preference */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Options DTE
            </Label>
            <Select 
              value={settings.dtePreference} 
              onValueChange={(value) => setSettings(prev => ({ ...prev, dtePreference: value }))}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select DTE" />
              </SelectTrigger>
              <SelectContent>
                {DTE_PREFERENCES.map((dte) => (
                  <SelectItem key={dte.value} value={dte.value}>
                    {dte.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Risk Per Trade */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
              <BarChart3 className="w-3 h-3" />
              Risk Per Trade
            </Label>
            <Select 
              value={settings.riskPerTrade} 
              onValueChange={(value) => setSettings(prev => ({ ...prev, riskPerTrade: value }))}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select risk %" />
              </SelectTrigger>
              <SelectContent>
                {RISK_PER_TRADE.map((risk) => (
                  <SelectItem key={risk.value} value={risk.value}>
                    {risk.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Quick Summary */}
        <div className={`${selectedStrategy.bgColor} p-3 rounded-lg border ${selectedStrategy.borderColor}`}>
          <p className="text-xs text-slate-700">
            <span className="font-semibold">Profile:</span>{' '}
            <span className={`font-medium ${selectedStrategy.color}`}>{selectedStrategy.label}</span>{' '}
            • <span className="font-medium">${parseInt(settings.accountSize).toLocaleString()}</span> account{' '}
            • <span className="font-medium">{settings.riskPerTrade}%</span> risk/trade{' '}
            • <span className="font-medium">{DTE_PREFERENCES.find(d => d.value === settings.dtePreference)?.label}</span> options
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default TradingProfileSettings;
